import React from 'react';

import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { Feature } from '@commonTuna/react/objects/CompanyFeature';

import CompanyFeatureAccess from '@app/components/UI/CompanyFeatureAccess/CompanyFeatureAccess';
import { usePhoneCallProviderContext, PhoneCallResponse } from '@app/components/UI/PhoneCallProvider/PhoneCallProvider';

interface Props {
	patientId?: number;
	inquiryId?: number;
	portalId?: number;
	chatId?: number;
	className?: string;
	patientNumber?: string;
	patientAvatar: string;
	patientName: string;
	title?: string;
	disabled?: boolean;
}

const CallPatient: React.FC<Props> = (props) => {
	const { state: { phoneCallState }, actions: { update } } = usePhoneCallProviderContext();
	const { request } = useApplicationContext();
	const { openErrorMessage } = useModal();

	const [hasDevice, setHasDevice] = React.useState<boolean>(false);
	const { isBusy } = phoneCallState;
	const isDisabled = React.useMemo(() => isBusy || !hasDevice, [hasDevice, isBusy]);
	const title = React.useMemo<string>(
		() =>
			(!hasDevice ? 'Input Microphone'
				: isBusy ? 'You have active call' : 'Call Patient'),
		[hasDevice, isBusy],
	);

	const {
		className,
		children,
		patientId,
		inquiryId,
		portalId,
		patientNumber,
		patientAvatar,
		patientName,
		chatId,
	} = props;

	const handleSuccess = (token: string, phoneNumber?: string) => {
		update({
			...phoneCallState,
			token,
			patientId,
			inquiryId,
			portalId,
			patientAvatar,
			patientNumber: phoneNumber || patientNumber,
			patientName,
			chatId,
			isActive: true,
			isBusy: true,
		});
	};

	const checkDevices = async () => {
		if (!navigator.mediaDevices) return Promise.resolve();
		const devices = await navigator.mediaDevices?.enumerateDevices();
		setHasDevice(devices.filter((device) => device.kind === 'audioinput').length > 0);
	};

	React.useEffect(() => {
		checkDevices().catch(openErrorMessage);
		navigator.mediaDevices?.addEventListener('devicechange', checkDevices);
		return () => navigator.mediaDevices?.removeEventListener('devicechange', checkDevices);
	}, []);

	const handleClick = (e) => {
		e.preventDefault();

		if (portalId) {
			// todo replace it with one call
			request<string>('getPatientPhoneNumber', { id: portalId })
				.then((phoneNumber) => {
					request<PhoneCallResponse>('phoneCallAccessToken', {})
						.then((res) => {
							handleSuccess(res.token, phoneNumber);
						})
						.catch(openErrorMessage);
				})
				.catch(openErrorMessage);
			return;
		}

		if (!patientNumber) {
			openErrorMessage?.('Phone number is not set');
			return;
		}

		request<PhoneCallResponse>('phoneCallAccessToken', {})
			.then((res) => handleSuccess(res.token))
			.catch((err) => openErrorMessage?.(err));
	};

	return (
		<CompanyFeatureAccess feature={Feature.SupportChats}>
			<button
				type="button"
				className={className}
				title={props.title ?? title}
				onClick={handleClick}
				disabled={props.disabled || isDisabled}
			>
				{children}
			</button>
		</CompanyFeatureAccess>
	);
};

export default CallPatient;
